import React, { useEffect, useState } from "react";
import { Sparkles, Save, RotateCcw } from "lucide-react";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { endpoints } from "../lib/api";
import { toast } from "sonner";

/**
 * Lightweight WhatsApp retention message templates + toggles.
 * No automation engine yet - these power click-to-chat suggestions in the
 * UI (e.g. on member profiles, dashboard task list, AI suggest fallback).
 */
const TEMPLATES = [
  { key: "birthday",          label: "Birthday wishes",       hint: "Sent on member birthday",            example: "Happy birthday {name}! Hope you crush a workout today and treat yourself a little. - {gym}" },
  { key: "anniversary",       label: "Membership anniversary",hint: "Sent on join anniversary",           example: "{name}, you've been with us for {years} year(s) - proud to have you in the {gym} family." },
  { key: "renewal_reminder",  label: "Renewal reminders",     hint: "Sent 7 / 2 / 0 days before expiry",  example: "Hey {name}, your {plan} plan expires in {days} days. Want to renew now and keep your streak alive?" },
  { key: "trial_reminder",    label: "Trial reminders",       hint: "Sent 1 day before trial",            example: "Looking forward to your trial tomorrow at {time}, {name}. Bring water and we'll handle the rest." },
  { key: "inactivity_nudge",  label: "Inactivity nudges",     hint: "Sent after 7 / 14 days no visit",    example: "Missed you at the gym this week, {name}. Want to come for an easy session today? Sometimes the hardest step is just walking in." },
  { key: "streak_achievement",label: "Streak achievements",   hint: "Sent on hitting 7d / 14d streak",    example: "{name}, that's a {days}-day streak - quietly proud of you. Keep showing up." },
  { key: "first_class",       label: "First class completed", hint: "Sent after first check-in",          example: "First one's done, {name}. The next one is the one that matters most. See you tomorrow?" },
  { key: "milestone",         label: "Membership milestones", hint: "Sent on 50/100 visits",              example: "{name}, you just hit {count} check-ins - that's not luck, that's discipline. Respect." },
];

export default function RetentionTemplatesSection({ settings, onSaved }) {
  const [toggles, setToggles] = useState({});
  const [templates, setTemplates] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setToggles(settings?.retention_toggles || {});
    setTemplates(settings?.retention_templates || {});
  }, [settings]);

  const setToggle = (key, v) => setToggles(prev => ({ ...prev, [key]: v }));
  const setTemplate = (key, v) => setTemplates(prev => ({ ...prev, [key]: v }));
  const resetToDefault = (key) => {
    const ex = TEMPLATES.find(t => t.key === key)?.example || "";
    setTemplate(key, ex);
  };

  const save = async () => {
    setBusy(true);
    try {
      await endpoints.updateSettings({
        ...settings,
        retention_toggles: toggles,
        retention_templates: templates,
      });
      toast.success("Retention templates saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="retention-templates" className="bg-card border border-border rounded-xl p-5 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-500" /> Retention messages
          </h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Toggle which moments trigger a click-to-chat WhatsApp suggestion on relevant screens.
            Use <code className="px-1 py-0.5 rounded bg-muted text-foreground font-mono text-[10px]">{"{name}"}</code>, <code className="px-1 py-0.5 rounded bg-muted text-foreground font-mono text-[10px]">{"{gym}"}</code>, <code className="px-1 py-0.5 rounded bg-muted text-foreground font-mono text-[10px]">{"{days}"}</code>, <code className="px-1 py-0.5 rounded bg-muted text-foreground font-mono text-[10px]">{"{plan}"}</code>, <code className="px-1 py-0.5 rounded bg-muted text-foreground font-mono text-[10px]">{"{time}"}</code> as placeholders.
          </p>
        </div>
        <button
          onClick={save}
          disabled={busy}
          data-testid="save-retention-templates"
          className="shrink-0 inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:opacity-90 text-xs font-semibold px-3 py-2 rounded-md disabled:opacity-60"
        >
          <Save className="w-3.5 h-3.5" /> Save
        </button>
      </div>

      <div className="space-y-3">
        {TEMPLATES.map(t => {
          const enabled = !!toggles[t.key];
          const text = templates[t.key] ?? t.example;
          return (
            <div key={t.key} className="rounded-lg border border-border p-3" data-testid={`retention-row-${t.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{t.label}</p>
                  <p className="text-[11px] text-muted-foreground/85">{t.hint}</p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={(v) => setToggle(t.key, !!v)}
                  data-testid={`retention-toggle-${t.key}`}
                />
              </div>
              {enabled && (
                <div className="mt-2">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Message</Label>
                  <Textarea
                    value={text}
                    onChange={(e) => setTemplate(t.key, e.target.value)}
                    data-testid={`retention-text-${t.key}`}
                    className="min-h-[64px] mt-1 text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => resetToDefault(t.key)}
                    className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-1"
                  >
                    <RotateCcw className="w-2.5 h-2.5" /> Reset to default
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
